import { ApiError } from "@/core/api/errors";
import type { components } from "@/core/api/schema";
import { formatDay } from "@/core/format/format";

export type Dish = components["schemas"]["Dish"];
export type DietType = Dish["dietType"];

/** The Indian food mark: a green dot for veg, brown for egg, red for non-veg (the label says it too). */
export const DIET_DISPLAY: Record<DietType, { label: string; dot: string; ring: string }> = {
  VEG: { label: "Veg", dot: "bg-primary", ring: "border-primary" },
  EGG: { label: "Egg", dot: "bg-warn", ring: "border-warn" },
  NON_VEG: { label: "Non-veg", dot: "bg-danger", ring: "border-danger" },
};
export const DIET_TYPES: readonly DietType[] = ["VEG", "EGG", "NON_VEG"];

/** The API's limits for a dish. */
export const DISH_LIMITS = {
  nameMax: 80,
  descriptionMax: 1000,
  urlMax: 500,
  caloriesMax: 5000,
  gramsMax: 1000,
  quantityMax: 10,
  addonsMax: 20,
} as const;

/** "420 kcal · P 22 g · C 50 g · F 12 g", leaving out what isn't known; null when nothing is. */
export function nutritionLine(dish: Pick<Dish, "calories" | "proteinG" | "carbsG" | "fatG">): string | null {
  const parts = [
    dish.calories !== null ? `${dish.calories} kcal` : null,
    dish.proteinG !== null ? `P ${dish.proteinG} g` : null,
    dish.carbsG !== null ? `C ${dish.carbsG} g` : null,
    dish.fatG !== null ? `F ${dish.fatG} g` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * A dish that can't be turned off or deleted (`DISH_IN_USE`): where it's still used, from the API's details,
 * so the team knows what to change first. Null for any other error.
 */
export function dishInUse(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "DISH_IN_USE") return null;
  const details = (error.details ?? {}) as { menuDates?: string[]; templates?: string[] };
  const where = [
    details.menuDates?.length ? `menus on ${details.menuDates.map(formatDay).join(", ")}` : null,
    details.templates?.length ? `templates ${details.templates.map((t) => `“${t}”`).join(", ")}` : null,
  ].filter(Boolean);
  return `Still used in ${where.join(" and ")}${(details.menuDates?.length ?? 0) >= 5 ? " (and maybe more)" : ""}. Replace it there first.`;
}
